<!--
  Placeholder strategy
    - a top discreet cloak spinner, no placeholder components
    - avoid flickering on showing content with a "fade" effect
    - adding an extra pre-buffer for <vue-pdf-embed> first print (see "./Main.vue")
-->
<template>
  <div class="page-details">

    <layout-teleport-to-breadcrumb>
      <layout-breadcrumb-loader
        :predicate="fetchState.status.value === 'success'"
      />
    </layout-teleport-to-breadcrumb>

    <ui-cloak
      :is-visible="fetchState.status.value === 'success'"
    >
      <template #default>

        <transition
          appear
          appear-active-class="animate__animated animate__fadeIn ease-in-out a1 d1"
        >
          <div class="page-details__main">

            <div class="page-details__header">

              <!-- Nav menu -->
              <layout-nav-tab-menu
                v-model="navSection"
                :items="navSectionOptions"
              >

                <template #left-side>
                  <ui-link-button-meek
                    :size="UI.SIZE.SMALL"
                    :route="parentRoute"
                    class="page-details__header__link"
                  >
                    <ui-icon
                      name="navigate_before"
                    />
                    Modèles de contrat
                  </ui-link-button-meek>
                </template>

                <template #right-side>
                  <ui-wad class="flex-grow-1 d-flex align-items-center justify-content-flex-end mr-1.5">

                    <ui-pending-icon
                      ref="pendingIcon"
                    />

                  </ui-wad>
                </template>
              </layout-nav-tab-menu>

            </div>

            <!-- TAB: Details -->
            <section
              v-show="navSection === NAV_SECTIONS.DETAILS"
              :key="NAV_SECTIONS.DETAILS"
              :class="[
                'page-details__tab',
                'page-details__tab--details'
              ]"
            >
              <details-main
                v-bind="{
                  updateCallback,
                  previewCallback,
                  topicList,
                  isPending,
                  data: details,
                  isReadonlyFlag,
                  customDataList,
                }"
                class="page-details__details-main"
                @on-next-buffer="callPendingIconSuccess"
              />
            </section>

            <!-- TAB: Settings -->
            <section
              v-show="navSection === NAV_SECTIONS.SETTINGS"
              :key="NAV_SECTIONS.SETTINGS"
              :class="[
                'page-details__tab',
                'page-details__tab--settings'
              ]"
            >

              <details-general
                class="page-details__general"
                v-bind="{
                  callback: updateDetailsCallback,
                  typeProfessionList,
                  isReadonlyFlag,
                }"
                :default-data="{
                  title: details.title,
                  code_type_profession: details.code_type_profession,
                }"
              />

            </section>

          </div>

        </transition>

      </template>
    </ui-cloak>

  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/sass/modules/layout/variables'

$side-menu-width: 240px

.page-details
  height: 100%

  &__main
    height: 100%
    display: flex
    flex-direction: column

  &__header
    padding-top: $layout-spacer-y
    background-color: $subheader-bg

    &__link
      // (empirical alignment with parent container gutter)
      $offset-y: -16px
      $margin-left: 12px

      align-self: center
      transform: translateY($offset-y)
      margin-left: $margin-left
      margin-right: $layout-spacer-x

  &__tab

    &--details
      position: relative
      height: 100%
      overflow-y: scroll

      // ("Law of Separation" between sheet tab and lateral sidemenu)
      border-left: 1px solid $gray-bright

    &--settings
      align-items: stretch
      flex-grow: 1
      padding: $layout-spacer-y $layout-spacer-x

  &__general
    width: map-get($breakpoints, 'mobile')

  /////////////////////////////////////////

  &__details-main
    @include inset
    position: absolute

</style>

<script lang="ts">
import DetailsMain from '~/components/hamac/contract-template/details/Main.vue'
import DetailsGeneral from '~/components/hamac/contract-template/details/General.vue'
import Console from '~/utils/lib/core/console'
import { join, merge, propEq, sustainedPromise, tapCatch, toString } from '~/utils/lib/core/prelude'
import { getListRoute } from '~/stores/hamac/contract-template/company/index'
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { useLegacyPageFetch } from '~/composables/core/hook'
import { useToastBuilder } from '~/composables/layout/toast'
import { useCompanyRequiredNavigationContext } from '~/composables/layout/navigation'
import type { ContractTemplate, ContractTemplateContent } from '~/stores/hamac/contract-template'
import type { UiPendingIcon } from '#components'
import { useStore_hamac_contractTemplate_company_details } from '~/stores/hamac/contract-template/company/details'
import { useStore_hamac_profession_typeProfession } from '~/stores/hamac/profession/type-profession'
import { useStore_hamac_contractTemplate_company_topic } from '~/stores/hamac/contract-template/company/topic'
import { useStore_hamac_contractTemplate_customData, type CustomData } from '~/stores/hamac/contract-template/custom-data'
import type { TypeProfession } from '~/stores/hamac/profession'
import type { Topic } from '~/stores/hamac/topic'
import { useLinkHandler } from '~/composables/core/link-handler'

const console = new Console('component:company-contract-template-details')

const NAV_SECTIONS = {
  DETAILS: 'details',
  SETTINGS: 'settings',
}

const SETTINGS_SECTIONS = {
  GENERAL: 'general',
  COMPANY: 'company',
}

export default defineComponent({
  components: { DetailsMain, DetailsGeneral },
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_REQUIRED,
      menuItem: MENU_ITEM.EMBAUCHE,
    })
    const navigationContext = useCompanyRequiredNavigationContext()

    useHead({
      title: `Modèle de contrat`,
    })

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const ToastBuilder = useToastBuilder()

    const ref_pendingIcon
      = useTemplateRef<InstanceType<typeof UiPendingIcon>>('pendingIcon')

    const store_contractTemplate_company_details = useStore_hamac_contractTemplate_company_details()
    const store_contractTemplate_company_topic = useStore_hamac_contractTemplate_company_topic()
    const store_contractTemplate_customData = useStore_hamac_contractTemplate_customData()
    const store_typeProfession = useStore_hamac_profession_typeProfession()

    return {
      ...navigationContext,
      ...useLinkHandler(),
      fetchState,
      fetchWrapper,
      ToastBuilder,
      ref_pendingIcon,
      store_contractTemplate_company_details,
      store_contractTemplate_company_topic,
      store_contractTemplate_customData,
      store_typeProfession,
    }
  },
  data() {
    const {
      structure: { id },
      company: { code_societe }
      // @ts-expect-error: "data" hook not injecting "setup" dependencies
    } = this.fromCompanyLevel()

    const parentRoute = getListRoute(id, code_societe)

    const navSectionOptions = [
      {
        id: NAV_SECTIONS.DETAILS,
        text: 'Détails'
      },
      {
        id: NAV_SECTIONS.SETTINGS,
        text: 'Paramètres'
      }
    ]

    return {
      NAV_SECTIONS,
      SETTINGS_SECTIONS,
      parentRoute,
      navSection: NAV_SECTIONS.DETAILS,
      navSectionOptions,
      topicList: [] as Topic[], // (lazy)
      typeProfessionList: [] as TypeProfession[], // (lazy)
      customDataList: [] as CustomData[], // (lazy)
      details: {} as ContractTemplate, // (lazy)
      isPending: false,
      isReadonlyFlag: false, // (lazy)
    }
  },
  created() {
    this.fetchWrapper(
      () => this.fetch()
    )
  },
  methods: {
    updateCallback(
      data :ContractTemplateContent
    ) {
      const opts = {
        data,
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        id: this.$route.params.contractTemplateId as string
      }

      this.isPending = true

      this.ref_pendingIcon?.isPending()

      return this.store_contractTemplate_company_details.updateContent(opts)
        .catch(err => {
          console.warn('update', err)

          this.isPending = false

          this.ref_pendingIcon?.onError()

          this.ToastBuilder
            .error(toString(err))
            .title(`Modification modèle de contrat`)
            .show()
        })
    },
    previewCallback() {
      const opts = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        id: this.$route.params.contractTemplateId as string
      }

      return this.store_contractTemplate_company_details.preview(opts)
        .catch(err => {
          console.warn('previewCallback', err)
          this.ToastBuilder
            // (?) As XHR returns a Blob response that is currently not yet
            //     handled, we just print a static message (KISS decision)
            .warning(`Erreur lors de la génération du PDF`)
            .title(`Aperçu d'un modèle de contrat`)
            .show()
        })
    },
    callPendingIconSuccess() {
      this.isPending = false

      this.ref_pendingIcon?.onSuccess()
    },
    updateDetailsCallback(data) {
      const opts = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        id: this.$route.params.contractTemplateId as string,
        data
      }

      return sustainedPromise(
          300,
          this.store_contractTemplate_company_details.updateDetails(opts)
        )
        .then(() => {
          // merge new updated properties to current details
          this.details = merge(this.details, data)

          this.ToastBuilder
            .success()
            .class('edit-model-toast')
            .title(`Informations modifiées`)
            .show()
        })
        .catch(err => {
          console.warn('update', err)
          this.ToastBuilder
            .error(toString(err))
            .class('edit-model-toast')
            .title(`Modification d'un modèle de contrat`)
            .show()
        })
    },
    fetch() {
      const uuidData = this.fromCompanyLevel().structure.uuidData
      const code_societe = this.fromCompanyLevel().company.code_societe
      const id = this.$route.params.contractTemplateId as string

      const input = {
        per_page: 10000 // eager
      }

      return Promise.all([
          this.store_contractTemplate_company_details.read(
            { uuidData, code_societe, id }
          ),
          this.store_typeProfession.read_(
            { uuidData }
          ),
          this.store_contractTemplate_company_topic.list(
            { uuidData, code_societe, input }
          ),
          this.store_contractTemplate_customData.read_(
            { uuidData }
          ),
        ])
        .then(results => {
          const details = results[0]
          const typeProfessionList = results[1]
          const topicList = results[2].data
          const isReadonly = propEq('is_read_only', true, details)
          const customDataList = results[3]

          // Page title
          document.title = join(' ', [
            `Modèle de contrat « ${details.title} »`,
            `— ${this.fromCompanyLevel().structure.raison}`,
          ])

          // Component data
          this.details = details
          this.typeProfessionList = typeProfessionList
          this.topicList = topicList
          this.isReadonlyFlag = isReadonly
          this.customDataList = customDataList
        })
        .catch(tapCatch(err => {
          console.warn(err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Détails d'un modèle de contrat`)
            .show()
        }))
    },
  }
})
</script>
